/* eslint-disable @typescript-eslint/no-unsafe-function-type */
import FormField from "@components/Form/FormFields/FormFields";
import formStyles from "@components/Form/FormFields/FormFields.module.scss";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import {
  FieldErrors,
  FieldValues,
  UseFormClearErrors,
  UseFormRegister,
  UseFormSetError,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import styles from "./CountryNumber.module.scss";

export interface OptionsDataTypes {
  code: string;
  dial_code: string;
  name: string;
}

export interface CountryNumberProps {
  register: UseFormRegister<FieldValues>;
  watch: UseFormWatch<FieldValues>;
  dialCodeLabelText: string;
  telephoneInputPlaceholderText: string;
  telephoneNumberHintText: string;
  optionsData: Array<OptionsDataTypes>;
  selectedDialCode: string;
  setSelectedDialCode: Function;
  numberValue: string;
  setNumberValue: Function;
  errors: FieldErrors;
  fieldIsRequired: boolean;

  setValue: UseFormSetValue<FieldValues>; // question -> should this be optional?
  setError: UseFormSetError<FieldValues>; // question -> should this be optional?
  clearErrors: UseFormClearErrors<FieldValues>; // question -> should this be optional?
}

const CountryNumber = (props: CountryNumberProps) => {
  const {
    register,
    watch,
    dialCodeLabelText,
    telephoneInputPlaceholderText,
    telephoneNumberHintText,
    optionsData,
    selectedDialCode,
    setSelectedDialCode,
    numberValue,
    setNumberValue,
    errors,
    fieldIsRequired,
    setValue,
    setError,
    clearErrors,
  } = props;

  const PREFIX_GAP = 6;
  const EMPTY_DIAL_PREFIX = `+${" ".repeat(PREFIX_GAP)}|${" ".repeat(3)}`;
  const DIAL_SEPARATOR = " |  ";
  const MIN_PHONE_DIGITS = 7;
  const MAX_PHONE_DIGITS = 15;
  const COUNTRY_CODE_FIELD_NAME = "SelectCountrydialCode";
  const TEL_ERROR_ID = "TelephoneNumber-error";
  const TEL_HINT_ID = "TelephoneNumber-hint";

  const numberInputSettings = {
    name: "telephone",
    type: "text",
    placeholder: telephoneInputPlaceholderText,
    hintText: telephoneNumberHintText,
    validation: {
      isRequired: fieldIsRequired,
      telephone: true,
    },
  };

  const inputValue = watch(numberInputSettings?.name, false);
  const hasError = errors && errors[numberInputSettings?.name];
  const [isFocused, setIsFocused] = useState(false);

  const phoneInputRef = useRef<HTMLInputElement | null>(null);

  const extractDigits = (value: string) => (value || "").replace(/\D/g, "");

  const formatPrefix = (dial: string) =>
    dial ? `${dial.replace("+", "+ ")}${DIAL_SEPARATOR}` : "";

  const getPrefix = () => {
    if (selectedDialCode) return formatPrefix(selectedDialCode);
    if (numberValue?.startsWith("+")) return EMPTY_DIAL_PREFIX;
    return "";
  };

  const extractUserDigits = (value: string, prefix: string) => {
    if (!value) return "";
    const trimmed =
      prefix && value.startsWith(prefix)
        ? value.slice(prefix.length)
        : value.replace(/^\+\s*\d*\s*\|\s*/, "");
    return extractDigits(trimmed);
  };

  const buildPhoneValue = (prefix: string, digits: string) =>
    `${prefix}${digits}`;

  const moveCaretAfterPrefix = (prefix: string) => {
    requestAnimationFrame(() => {
      const input = phoneInputRef.current;
      if (!input) return;
      input.setSelectionRange(prefix.length, prefix.length);
    });
  };

  const focusPhoneAfterPrefix = (prefix: string) => {
    requestAnimationFrame(() => {
      phoneInputRef.current?.focus();
      moveCaretAfterPrefix(prefix);
    });
  };

  const handleCountryCodeChange = (event) => {
    const newDial = event.target.selectedOptions[0]?.id || "";

    // explicitly compute the *old* prefix we should strip
    const oldPrefix = selectedDialCode
      ? formatPrefix(selectedDialCode)
      : numberValue?.startsWith("+")
        ? EMPTY_DIAL_PREFIX
        : "";

    let digits = extractUserDigits(numberValue, oldPrefix);

    // No dial selected (empty option)
    if (!newDial) {
      setSelectedDialCode("");
      clearErrors(COUNTRY_CODE_FIELD_NAME);

      const formatted = buildPhoneValue(EMPTY_DIAL_PREFIX, digits);

      setNumberValue(formatted);
      setValue("telephone", formatted, {
        shouldDirty: true,
        shouldValidate: true,
      });

      setIsFocused(true);
      focusPhoneAfterPrefix(EMPTY_DIAL_PREFIX);
      return;
    }

    // prevent duplicates
    const newDialDigits = extractDigits(newDial);
    const userTypedCountryFirst =
      !selectedDialCode && oldPrefix === EMPTY_DIAL_PREFIX;

    if (userTypedCountryFirst && digits.startsWith(newDialDigits)) {
      digits = digits.slice(newDialDigits.length);
    }

    setSelectedDialCode(newDial);
    clearErrors(COUNTRY_CODE_FIELD_NAME);

    // explicitly compute the *new* prefix
    const newPrefix = formatPrefix(newDial);
    const formatted = buildPhoneValue(newPrefix, digits);

    setNumberValue(formatted);
    setValue("telephone", formatted, {
      shouldDirty: true,
      shouldValidate: true,
    });

    setIsFocused(true);
    focusPhoneAfterPrefix(newPrefix);
  };

  const handleOnFocus = () => {
    setIsFocused(true);

    const prefix = selectedDialCode ? getPrefix() : EMPTY_DIAL_PREFIX;

    if (!numberValue) {
      setNumberValue(prefix);
      moveCaretAfterPrefix(prefix);
      return;
    }

    if (selectedDialCode && !numberValue.startsWith(getPrefix())) {
      const digits = extractUserDigits(numberValue, getPrefix());
      const newPrefix = getPrefix();
      setNumberValue(buildPhoneValue(newPrefix, digits));
      moveCaretAfterPrefix(newPrefix);

      return;
    }

    moveCaretAfterPrefix(prefix);
  };

  const handleOnBlur = () => {
    if (numberValue) {
      setIsFocused(true);
      return;
    }
    setIsFocused(false);
  };

  useEffect(() => {
    if (optionsData) {
      setIsFocused(false);
    } else {
      if (inputValue) {
        setIsFocused(true);
      }
    }
  }, [optionsData]);

  useEffect(() => {
    setIsFocused(!!numberValue);
  }, [numberValue]);

  const validationRules = {
    validate: {
      required: (value: string) => {
        if (!fieldIsRequired) return true;

        const prefix =
          getPrefix() || (value?.startsWith("+") ? EMPTY_DIAL_PREFIX : "");
        const digits = extractUserDigits(value, prefix);

        if (!digits) return `${telephoneInputPlaceholderText} is required`;
        return true;
      },

      length: (value: string) => {
        const prefix =
          getPrefix() || (value?.startsWith("+") ? EMPTY_DIAL_PREFIX : "");
        const digits = extractUserDigits(value, prefix);

        if (!digits) return true;

        if (
          digits.length < MIN_PHONE_DIGITS ||
          digits.length > MAX_PHONE_DIGITS
        ) {
          return `Phone number must contain ${MIN_PHONE_DIGITS} to ${MAX_PHONE_DIGITS} digits`;
        }

        return true;
      },
    },
  };

  function transformArray(inputArray) {
    return inputArray.map((item) => ({
      value: `${item.name} ${item.dial_code}`,
      id: item.dial_code,
    }));
  }
  const transformed = transformArray(optionsData);

  const selectedOptionValue = selectedDialCode
    ? (transformed.find((o) => o.id === selectedDialCode)?.value ?? "")
    : "";

  return (
    <div
      onBlur={handleOnBlur}
      className={clsx(
        "form",
        "form-row",
        formStyles["form-row"],
        styles["form-row"],
        styles["countryCodeAndPhone"],
        isFocused && "focused",
        isFocused && formStyles.focused,
        hasError && "error",
        hasError && formStyles.error
      )}
    >
      <div className={clsx(styles["base6"], styles.countrycodefield)}>
        <FormField
          formType="select"
          name={COUNTRY_CODE_FIELD_NAME}
          onChange={handleCountryCodeChange}
          register={register}
          watch={watch}
          options={transformed}
          isEmptyFirstOption={true}
          selectValue={selectedOptionValue}
          placeholder={dialCodeLabelText}
          validation={{
            isRequired: fieldIsRequired,
          }}
          errors={errors}
        />
      </div>
      <div className={clsx(styles["base6"], styles.telfield)}>
        <div className={clsx(formStyles["form-row"], "field-row")}>
          <div className={formStyles["form-label"]}>
            <div className={formStyles.label}>
              <label htmlFor="TelephoneNumber">
                {telephoneInputPlaceholderText}
              </label>
            </div>
          </div>
          <div className={formStyles["form-field"]}>
            <div className={formStyles.field}>
              <input
                id="TelephoneNumber"
                aria-invalid={!!errors[numberInputSettings.name] || undefined}
                aria-required={fieldIsRequired}
                aria-describedby={
                  errors[numberInputSettings.name]?.message
                    ? TEL_ERROR_ID
                    : telephoneNumberHintText
                      ? TEL_HINT_ID
                      : undefined
                }
                ref={(el) => {
                  phoneInputRef.current = el;
                }}
                onFocus={handleOnFocus}
                onClick={() => {
                  const input = phoneInputRef.current;
                  if (!input) return;

                  const prefix = selectedDialCode
                    ? getPrefix()
                    : EMPTY_DIAL_PREFIX;
                  if (!prefix) return;

                  requestAnimationFrame(() => {
                    const pos = input.selectionStart ?? 0;
                    if (pos < prefix.length) {
                      input.setSelectionRange(prefix.length, prefix.length);
                    }
                  });
                }}
                onKeyDown={(e) => {
                  const input = phoneInputRef.current;
                  if (!input) return;

                  const prefix = selectedDialCode
                    ? getPrefix()
                    : EMPTY_DIAL_PREFIX;
                  if (!prefix) return;

                  const start = input.selectionStart ?? 0;
                  const end = input.selectionEnd ?? 0;

                  const isBackspaceInPrefix =
                    e.key === "Backspace" && start <= prefix.length;
                  const isDeleteInPrefix =
                    e.key === "Delete" && start < prefix.length;
                  const isSelectionInPrefix =
                    start < prefix.length && end <= prefix.length;

                  if (
                    isBackspaceInPrefix ||
                    isDeleteInPrefix ||
                    isSelectionInPrefix
                  ) {
                    e.preventDefault();
                    input.setSelectionRange(prefix.length, prefix.length);
                  }
                }}
                value={numberValue}
                type="text"
                {...register(numberInputSettings?.name, {
                  ...validationRules,
                  onChange: (event) => {
                    const raw = event.target.value as string;

                    const prefix = selectedDialCode
                      ? getPrefix()
                      : raw.startsWith("+")
                        ? EMPTY_DIAL_PREFIX
                        : "";

                    const digits = extractUserDigits(raw, prefix);
                    const formatted = buildPhoneValue(prefix, digits);

                    setNumberValue(formatted);

                    setValue("telephone", formatted, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  },

                  onBlur: () => {
                    const prefix =
                      getPrefix() ||
                      (numberValue?.startsWith("+") ? EMPTY_DIAL_PREFIX : "");
                    const digits = extractUserDigits(numberValue, prefix);

                    // Only enforce dial code when this field is required
                    if (fieldIsRequired && digits && !selectedDialCode) {
                      setError(COUNTRY_CODE_FIELD_NAME, {
                        type: "required",
                        message: "Country code is required",
                      });
                    } else {
                      clearErrors(COUNTRY_CODE_FIELD_NAME);
                    }
                  },
                })}
              />

              {(errors[numberInputSettings?.name]?.message ||
                numberInputSettings?.hintText) && (
                <>
                  <div
                    className={clsx(
                      formStyles["message-wrapper"],
                      "field",
                      "message-wrapper",
                      "inline-message",
                      errors[numberInputSettings?.name] && "field_error_msg"
                    )}
                  >
                    <p
                      id={
                        errors[numberInputSettings.name]?.message
                          ? TEL_ERROR_ID
                          : TEL_HINT_ID
                      }
                      className={clsx(
                        formStyles.message,
                        "message",
                        errors[numberInputSettings?.name]?.message && "active",
                        errors[numberInputSettings?.name]?.message &&
                          formStyles.active
                      )}
                    >
                      {/* @ts-expect-error type mismatch */}
                      {errors[numberInputSettings?.name]?.message
                        ? // ? String(errors[numberInputSettings?.name]?.message) -> can we do this instead?
                          errors[numberInputSettings?.name]?.message
                        : numberInputSettings?.hintText}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CountryNumber;
